import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  Grid,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  calcInputs,
  calcInternalInputs,
  computeSuggestedCluster,
  CALCULATOR_MODE,
} from '@siren-frontend/shared';
import PropTypes from 'prop-types';
import { useState } from 'react';

import CalculatorProviderSelector from './CalculatorProviderSelector';
import ComparePanel from './ComparePanel';
import DetailsPanel from './DetailsPanel';
import CalculatorInputsPanel from './inputs/CalculatorInputsPanel';

import './calculator.css';

function populateInputDefaults(inputs) {
  return Object.fromEntries(
    Object.entries(inputs).map(([inputKey, input]) => [
      inputKey,
      { ...input, value: input.default },
    ])
  );
}

const REPLICATION_FACTOR = 3;

function mergeDefaultsWithSearchParams(calcInputs) {
  const searchParams = new URLSearchParams(window.location.search); // when embedded has to be changed to window.parent

  return Object.fromEntries(
    Object.entries(calcInputs).map(([k, v]) =>
      searchParams.has(k)
        ? [
            k,
            {
              ...v,
              default: isNaN(searchParams.get(k))
                ? searchParams.get(k)
                : Number(searchParams.get(k)),
            },
          ]
        : [k, v]
    )
  );
}

function PricingCalculator({ instances, isLoading, error }) {
  const [mode, setMode] = useState(CALCULATOR_MODE.DETAILS);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const [calculatorInputs, setCalculatorInputs] = useState(
    populateInputDefaults(mergeDefaultsWithSearchParams(calcInputs))
  );

  const [calculatorInternalInputs, setCalculatorInternalInputs] = useState(
    populateInputDefaults(calcInternalInputs)
  );

  function setInput(inputName, inputValue) {
    const updateFn = calculatorInputs[inputName]
      ? setCalculatorInputs
      : setCalculatorInternalInputs;

    updateFn(prevState => ({
      ...prevState,
      [inputName]: {
        ...prevState[inputName],
        value: isNaN ? inputValue : Number(inputValue),
      },
    }));
  }

  const workload = Object.fromEntries(
    Object.entries(calculatorInputs).map(([specId, specValues]) => [
      specId,
      specValues.value || specValues.default,
    ])
  );

  const internalParamValues = Object.fromEntries(
    Object.entries(calculatorInternalInputs).map(([specId, specValues]) => [
      specId,
      specValues.value || specValues.default,
    ])
  );

  const calculatorResult = !isLoading
    ? computeSuggestedCluster(
        workload,
        REPLICATION_FACTOR,
        instances,
        calculatorInputs.cloudProvider.value,
        internalParamValues
      )
    : { prices: [], cluster: {} };

  function copyPermalink() {
    const paramsToCopy = [
      'cloudProvider',
      'itemSize',
      'reads',
      'writes',
      'storage',
    ];

    // const url = new URL(window.top.location.href); // Violates Same-Origin Policy
    const url = new URL(process.env.calculatorGetPricingUrl);

    for (const [k, v] of Object.entries(calculatorInputs)) {
      if (paramsToCopy.includes(k)) {
        url.searchParams.set(k, v.value.toString());
      }
    }

    navigator.clipboard.writeText(url.toString()).then(() => {
      setTooltipOpen(true);
      setTimeout(() => {
        setTooltipOpen(false);
      }, 2000);
    });
  }

  return (
    <Box className="calculator">
      <Grid container className="grid-container">
        <Grid
          item
          sm={12}
          md={5}
          p={4.5}
          className="left-panel"
          id="input-panel"
        >
          <CalculatorProviderSelector
            selectedProvider={calculatorInputs.cloudProvider.value}
            changeProvider={newProvider => {
              setInput('cloudProvider', newProvider);
            }}
          />
          <CalculatorInputsPanel
            calculatorInputs={calculatorInputs}
            calculatorInternalInputs={calculatorInternalInputs}
            onChange={setInput}
          />
        </Grid>
        <Grid
          item
          sm={12}
          md={7}
          className="flex-container aligned-center justified-center right-panel"
          id="result-panel"
        >
          {isLoading && (
            <Box>
              <CircularProgress data-loading="true" />
            </Box>
          )}
          {!isLoading && mode === CALCULATOR_MODE.DETAILS && (
            <DetailsPanel
              calculatorResult={calculatorResult}
              calculatorInputs={calculatorInputs}
              internalParamValues={internalParamValues}
              isLoading={isLoading}
              setInputFn={setInput}
              setMode={setMode}
              error={error}
            />
          )}
          {!isLoading && mode !== CALCULATOR_MODE.DETAILS && (
            <ComparePanel
              mode={mode}
              setMode={setMode}
              calculatorResult={calculatorResult}
              calculatorInputs={calculatorInputs}
            />
          )}
        </Grid>
      </Grid>
      <Box className="flex-container justified-center footer">
        <Typography>
          Pricing Starting from date: Nov 15, 2021. Apache® and Apache
          Cassandra® are either registered trademarks or trademarks of the
          Apache Software Foundation in the United States and/or other
          countries. Datastax is a trademark of Datastax inc. Amazon DynamoDB®
          is a trademark of Amazon.com, Inc. Azure CosmosDB is a trademark of
          the Microsoft corporation. No endorsements by The Apache Software
          Foundation, Datastax, Microsoft corporation or Amazon.com, Inc. are
          implied by the use of these marks.
        </Typography>
        <Tooltip
          title="Copied to clipboard"
          open={tooltipOpen}
          placement="top"
          arrow
        >
          <Button onClick={copyPermalink} className="button permalink-button">
            Share this price <LinkIcon />
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
}

PricingCalculator.propTypes = {
  instances: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  classes: PropTypes.shape({}),
};

export default PricingCalculator;
